import * as React from 'react'
import { Plus, Trash2, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { PlanPrice, PlanPriceCreate } from '@/lib/api'

const CURRENCIES = ['EUR', 'CZK', 'USD', 'GBP'] as const

interface PricingMatrixProps {
  prices: PlanPrice[]
  onChange: (prices: PlanPrice[]) => void
  onPriceCreate?: (price: PlanPriceCreate) => void
  onPriceDelete?: (priceId: string) => void
  className?: string
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button type="button" variant="ghost" size="sm" onClick={handleCopy} className="h-6 w-6 p-0">
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  )
}

export function PricingMatrix({
  prices,
  onChange,
  onPriceCreate,
  onPriceDelete,
  className,
}: PricingMatrixProps) {
  const [newCurrency, setNewCurrency] = React.useState<string>('')
  const [newMonthly, setNewMonthly] = React.useState('')
  const [newYearly, setNewYearly] = React.useState('')

  // Group prices by currency
  const pricesByCurrency = React.useMemo(() => {
    const grouped: Record<string, { monthly?: PlanPrice; yearly?: PlanPrice }> = {}

    for (const price of prices) {
      if (!grouped[price.currency]) {
        grouped[price.currency] = {}
      }
      const currencyGroup = grouped[price.currency]
      if (currencyGroup) {
        currencyGroup[price.interval as 'monthly' | 'yearly'] = price
      }
    }

    return grouped
  }, [prices])

  const existingCurrencies = Object.keys(pricesByCurrency)
  const availableCurrencies = CURRENCIES.filter(c => !existingCurrencies.includes(c))

  const handlePriceChange = (priceId: string, field: 'amount' | 'is_active', value: string | boolean) => {
    const updated = prices.map(p => {
      if (p.id === priceId) {
        return { ...p, [field]: value }
      }
      return p
    })
    onChange(updated)
  }

  const handleAddCurrency = () => {
    if (!newCurrency || !newMonthly) return

    // Create both monthly and yearly prices
    if (onPriceCreate) {
      onPriceCreate({
        currency: newCurrency,
        interval: 'monthly',
        amount: newMonthly,
        is_active: true,
      })
      if (newYearly) {
        onPriceCreate({
          currency: newCurrency,
          interval: 'yearly',
          amount: newYearly,
          is_active: true,
        })
      }
    }

    setNewCurrency('')
    setNewMonthly('')
    setNewYearly('')
  }

  const handleDeleteCurrency = (currency: string) => {
    const currencyPrices = pricesByCurrency[currency]
    if (currencyPrices?.monthly && onPriceDelete) {
      onPriceDelete(currencyPrices.monthly.id)
    }
    if (currencyPrices?.yearly && onPriceDelete) {
      onPriceDelete(currencyPrices.yearly.id)
    }
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Prices table */}
      <div className="rounded-lg border">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left text-sm font-medium">Currency</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Monthly</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Yearly</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Stripe IDs</th>
              <th className="px-4 py-3 text-center text-sm font-medium w-20">Active</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {existingCurrencies.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No prices configured. Add a currency below.
                </td>
              </tr>
            ) : (
              existingCurrencies.map((currency) => {
                const currencyPrices = pricesByCurrency[currency]
                const monthly = currencyPrices?.monthly
                const yearly = currencyPrices?.yearly
                const isActive = monthly?.is_active || yearly?.is_active

                return (
                  <tr key={currency} className="border-b last:border-b-0">
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="font-mono">
                        {currency}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {monthly ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={monthly.amount}
                          onChange={(e) => handlePriceChange(monthly.id, 'amount', e.target.value)}
                          className="w-28"
                        />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {yearly ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={yearly.amount}
                          onChange={(e) => handlePriceChange(yearly.id, 'amount', e.target.value)}
                          className="w-28"
                        />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {monthly?.stripe_price_id && (
                          <div className="flex items-center gap-1">
                            <code className="text-xs text-muted-foreground truncate max-w-32">
                              {monthly.stripe_price_id}
                            </code>
                            <CopyButton text={monthly.stripe_price_id} />
                          </div>
                        )}
                        {yearly?.stripe_price_id && (
                          <div className="flex items-center gap-1">
                            <code className="text-xs text-muted-foreground truncate max-w-32">
                              {yearly.stripe_price_id}
                            </code>
                            <CopyButton text={yearly.stripe_price_id} />
                          </div>
                        )}
                        {!monthly?.stripe_price_id && !yearly?.stripe_price_id && (
                          <span className="text-xs text-muted-foreground">No Stripe</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => {
                          if (monthly) handlePriceChange(monthly.id, 'is_active', e.target.checked)
                          if (yearly) handlePriceChange(yearly.id, 'is_active', e.target.checked)
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCurrency(currency)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add new currency */}
      {availableCurrencies.length > 0 && (
        <div className="flex items-end gap-3 pt-2 border-t">
          <div className="space-y-1">
            <label className="text-sm font-medium">Currency</label>
            <select
              value={newCurrency}
              onChange={(e) => setNewCurrency(e.target.value)}
              className="flex h-9 w-24 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Select</option>
              {availableCurrencies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Monthly</label>
            <Input
              type="number"
              step="0.01"
              placeholder="9.00"
              value={newMonthly}
              onChange={(e) => setNewMonthly(e.target.value)}
              className="w-28"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Yearly</label>
            <Input
              type="number"
              step="0.01"
              placeholder="90.00"
              value={newYearly}
              onChange={(e) => setNewYearly(e.target.value)}
              className="w-28"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleAddCurrency}
            disabled={!newCurrency || !newMonthly}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Currency
          </Button>
        </div>
      )}
    </div>
  )
}
